import { Logic } from '~common/ui-logic/logic'
import {
    SvgRendererProps,
    SvgGradient,
    SvgLinearGradient,
    SvgRadialGradient,
} from './types'

export interface SvgRendererDependencies extends SvgRendererProps {}

export interface SvgRendererState {
    svgContent: string | null
    error: Error | null
}

export class SvgRendererLogic extends Logic<
    SvgRendererDependencies,
    SvgRendererState
> {
    getInitialState = (): SvgRendererState => ({
        svgContent: null,
        error: null,
    })

    async loadSvg(url: string): Promise<void> {
        this.setState({ error: null })

        try {
            const response = await fetch(url)
            if (!response.ok) {
                throw new Error(`Failed to load SVG: ${response.statusText}`)
            }
            const svgText = await response.text()
            this.setState({ svgContent: svgText })
            this.deps.onLoad?.()
        } catch (error) {
            const err =
                error instanceof Error ? error : new Error('Unknown error')
            this.setState({ error: err })
            this.deps.onError?.(err)
        }
    }

    processSvg(
        svgContent: string,
        strokeColor?: string | SvgGradient,
        fillColor?: string | SvgGradient,
    ): string {
        if (!strokeColor && !fillColor) return svgContent

        let processedSvg = svgContent
        const gradients: SvgGradient[] = []

        // Collect gradients from stroke and fill
        if (typeof strokeColor === 'object') gradients.push(strokeColor)
        if (typeof fillColor === 'object') gradients.push(fillColor)

        // Inject gradient definitions if any gradients are provided
        if (gradients.length > 0) {
            processedSvg = this.injectGradients(processedSvg, gradients)
        }

        // Apply stroke color/gradient
        if (strokeColor) {
            const strokeValue =
                typeof strokeColor === 'string'
                    ? strokeColor
                    : `url(#${strokeColor.id})`
            processedSvg = this.applyColorAttribute(
                processedSvg,
                'stroke',
                strokeValue,
            )
        }

        // Apply fill color/gradient
        if (fillColor) {
            const fillValue =
                typeof fillColor === 'string'
                    ? fillColor
                    : `url(#${fillColor.id})`
            processedSvg = this.applyColorAttribute(
                processedSvg,
                'fill',
                fillValue,
            )
        }

        return processedSvg
    }

    private injectGradients(
        svgContent: string,
        gradients: SvgGradient[],
    ): string {
        const defsMatch = svgContent.match(/<defs[^>]*>[\s\S]*?<\/defs>/)
        let processedSvg = svgContent

        // Generate gradient elements
        const gradientElements = gradients
            .map((gradient) => this.createGradientElement(gradient))
            .join('')

        if (defsMatch) {
            // Inject gradients into existing defs
            const defsContent = defsMatch[0]
            processedSvg = svgContent.replace(
                defsMatch[0],
                defsContent.replace('</defs>', `${gradientElements}</defs>`),
            )
        } else {
            // Create defs with gradients if it doesn't exist
            const svgMatch = svgContent.match(/(<svg[^>]*>)/)
            if (svgMatch) {
                processedSvg = svgContent.replace(
                    svgMatch[1],
                    `${svgMatch[1]}<defs>${gradientElements}</defs>`,
                )
            }
        }

        return processedSvg
    }

    private createGradientElement(gradient: SvgGradient): string {
        const stops = gradient.stops
            .map(
                (stop) =>
                    `<stop offset="${stop.offset}" stop-color="${stop.color}"${stop.opacity ? ` stop-opacity="${stop.opacity}"` : ''}/>`,
            )
            .join('')

        if (gradient.type === 'linear') {
            const {
                id,
                x1 = 0,
                y1 = 0,
                x2 = '100%',
                y2 = 0,
                gradientUnits = 'objectBoundingBox',
            } = gradient
            return `<linearGradient id="${id}" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" gradientUnits="${gradientUnits}">${stops}</linearGradient>`
        } else {
            const {
                id,
                cx = '50%',
                cy = '50%',
                r = '50%',
                fx,
                fy,
                gradientUnits = 'objectBoundingBox',
            } = gradient
            return `<radialGradient id="${id}" cx="${cx}" cy="${cy}" r="${r}"${fx ? ` fx="${fx}"` : ''}${fy ? ` fy="${fy}"` : ''} gradientUnits="${gradientUnits}">${stops}</radialGradient>`
        }
    }

    private applyColorAttribute(
        svgContent: string,
        attribute: 'stroke' | 'fill',
        value: string,
    ): string {
        let processedSvg = svgContent

        // Replace existing attributes
        const regex = new RegExp(`${attribute}="[^"]*"`, 'g')
        processedSvg = processedSvg.replace(regex, `${attribute}="${value}"`)

        // Add attribute to elements that don't have it
        processedSvg = processedSvg.replace(
            /(<(path|circle|rect|line|polyline|polygon)[^>]*)(>)/g,
            (match, before, tag, after) => {
                if (!before.includes(`${attribute}=`)) {
                    return `${before} ${attribute}="${value}"${after}`
                }
                return match
            },
        )

        return processedSvg
    }
}
